import React, { useState, useEffect, useRef } from 'react';
import { supabase, checkSubscription, startHeartbeat, registerAuthSession, createPayment, createSubscription, ensureUserAndMerge, isQuestionsAdminPhone } from './supabaseClient';
import LangToggle from './LangToggle';
import PhoneAuth from './PhoneAuth';
// import Onboarding from './Onboarding';
import TrialQuiz from './TrialQuiz';
import PaymentScreen from './PaymentScreen';
import PlansScreen from './PlansScreen';
import HomeScreen from './HomeScreen';
import StudyScreen from './StudyScreen';
import SubjectScreen from './SubjectScreen';
import ErrorBoundary from './ErrorBoundary';
import AdminScreen from './AdminScreen';
import ExamScreen from './ExamScreen';
import WrongAnswersScreen from './WrongAnswersScreen';
import TajamuatScreen from './TajamuatScreen';
import CirclesScreen from './CirclesScreen';

const ADMIN_PHONES = (process.env.REACT_APP_ADMIN_PHONES || '').split(',').map(p => p.trim()).filter(Boolean);
const ADMIN_PLAN = { id: 'elite', name: 'elite', name_ar: 'elite', type: 'elite' };

function isAdmin(user) {
  if (!user) return false;
  const phone = (user.phone || '').replace(/\s/g, '');
  return ADMIN_PHONES.some(a => phone.endsWith(a.replace(/^\+966|^966|^0/, '')));
}

function AppInner() {
  const [user, setUser] = useState(null);
  const [stage, setStage] = useState('quiz');
  const [plan, setPlan] = useState(null);
  const [adminRestricted, setAdminRestricted] = useState(false);
  const [canAdmin, setCanAdmin] = useState(false);
  const GUEST_USER = { id: 'guest', phone: 'ضيف', isGuest: true };
  const GUEST_PLAN = { id: 'guest', name: 'ضيف', name_ar: 'ضيف' };
  const [quizScore, setQuizScore] = useState(null);
  const [checking, setChecking] = useState(true);
  const [studyMode, setStudyMode]       = useState(null);
  const [studySubject, setStudySubject] = useState(null);
  const heartbeatRef = useRef(null);

  // ── تتبع الزوار (مرة واحدة لكل session) ──
  useEffect(() => {
    if (sessionStorage.getItem('qadder_tracked')) return;
    let vid = localStorage.getItem('qadder_visitor_id');
    if (!vid) { vid = crypto.randomUUID(); localStorage.setItem('qadder_visitor_id', vid); }
    fetch('/api/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: vid }),
    }).catch(() => {});
    sessionStorage.setItem('qadder_tracked', '1');
  }, []);

  // ── الثيم: داكن دائماً ──
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'dark');
  }, []);
  const theme = 'dark';
  const setTheme = undefined;

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          setUser(session.user);
          // تأكد من وجود المستخدم في public.users وانقل أي اشتراك أدمن
          await ensureUserAndMerge(session.user);
          // سجّل جلسة جديدة دائماً عند كل تحميل للصفحة
          await registerAuthSession(session.user.id);

          // ── كشف عودة المستخدم من 3DS (موياسر يُضيف ?id=&status= في URL) ──
          const urlParams = new URLSearchParams(window.location.search);
          const moyasarId  = urlParams.get('id');
          const pending = JSON.parse(localStorage.getItem('qadder_pending_payment') || 'null');

          if (moyasarId && pending && pending.userId === session.user.id) {
            // نظّف URL بدون إعادة تحميل
            window.history.replaceState({}, '', '/');
            try {
              // تحقق server-side — لا نثق بـ status=paid من URL
              const verifyRes = await fetch('/api/verify-payment', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ paymentId: moyasarId }),
              });
              const verifyData = await verifyRes.json();
              if (!verifyData.ok) { setStage('plans'); return; }

              const { data: paymentRecord } = await createPayment(
                pending.userId, pending.planId, pending.amountSar, moyasarId
              );
              await createSubscription(pending.userId, pending.planId, paymentRecord.id);
              localStorage.removeItem('qadder_pending_payment');
              const { data: sub } = await checkSubscription(session.user.id);
              if (sub) { setPlan(sub.plans); setStage('home'); }
              else { setStage('plans'); }
            } catch {
              setStage('plans');
            }
            heartbeatRef.current = startHeartbeat(session.user.id);
            return;
          }

          // ── تدفق عادي ──
          if (isAdmin(session.user)) {
            setPlan(ADMIN_PLAN);
            setAdminRestricted(false);
            setCanAdmin(true);
            setStage('admin');
          } else if (await isQuestionsAdminPhone(session.user.phone)) {
            setPlan(ADMIN_PLAN);
            setAdminRestricted(true);
            setCanAdmin(true);
            setStage('admin');
          } else {
            const { data: sub } = await checkSubscription(session.user.id);
            if (sub) { setPlan(sub.plans); setStage('home'); }
            else { setStage('plans'); }
          }
          heartbeatRef.current = startHeartbeat(session.user.id);
        } else {
          setStage('quiz');
        }
      } catch (e) { /* ignore */ }
      finally { setChecking(false); }
    };
    checkSession();
    return () => { if (heartbeatRef.current) clearInterval(heartbeatRef.current); };
  }, []);

  if (checking) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f0c2e 0%, #1a1060 50%, #2d1b8e 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#f0b429', fontFamily: "'Tajawal', sans-serif", fontSize: '18px',
      }}>جاري التحميل...</div>
    );
  }

  if (stage === 'auth') {
    return (
      <PhoneAuth
        theme={theme}
        setTheme={setTheme}
        onGuest={() => {
          setUser(GUEST_USER);
          setPlan(GUEST_PLAN);
          setStage('home');
        }}
        onSuccess={async (u) => {
        setUser(u);
if (isAdmin(u)) {
          setPlan(ADMIN_PLAN);
          setAdminRestricted(false);
          setCanAdmin(true);
          setStage('admin');
        } else if (await isQuestionsAdminPhone(u.phone)) {
          setPlan(ADMIN_PLAN);
          setAdminRestricted(true);
          setCanAdmin(true);
          setStage('admin');
        } else {
          const { data: sub } = await checkSubscription(u.id);
          if (sub) {
            setPlan(sub.plans);
            setStage('home');
          } else {
            setStage('plans');
          }
        }
        if (u?.id) heartbeatRef.current = startHeartbeat(u.id);
      }} />
    );
  }

  if (stage === 'quiz') {
    return (
      <TrialQuiz
        theme={theme}
        setTheme={setTheme}
        onDone={(score) => { setQuizScore(score); setStage(user && plan ? 'home' : user ? 'plans' : 'auth'); }}
        onSkip={() => setStage(user ? 'home' : 'auth')}
        onLogin={() => setStage('auth')}
      />
    );
  }

  if (stage === 'plans') {
    return (
      <PlansScreen
        quizScore={quizScore}
        onSelect={(p) => { setPlan(p); setStage('payment'); }}
        onBack={() => user ? setStage('home') : setStage('quiz')}
      />
    );
  }

  if (stage === 'payment') {
    return (
      <PaymentScreen
        plan={plan}
        user={user}
        onSuccess={(p) => { setPlan(p); setStage('home'); }}
        onBack={() => setStage('plans')}
        onHome={() => setStage('home')}
      />
    );
  }

  if (stage === 'tajamuat') {
    return (
      <TajamuatScreen
        onSelect={({ mode, subject, subjects }) => {
          setStudyMode('tajamuat');
          setStudySubject({ subject, subjects });
          setStage('circles');
        }}
        onBack={() => setStage('home')}
      />
    );
  }

  if (stage === 'circles') {
    return (
      <CirclesScreen
        mode={studyMode}
        subjects={studySubject?.subjects || [studySubject?.subject]}
        plan={plan}
        onStartBatch={(batch) => {
          setStudySubject(prev => ({ ...prev, circlesBatch: batch }));
          setStage('study');
        }}
        onBack={() => setStage('tajamuat')}
        onPlans={() => setStage('plans')}
      />
    );
  }

  if (stage === 'subject') {
    return (
      <SubjectScreen
        onSelect={({ subject, section }) => {
          setStudySubject({ subject, section });
          setStage('study');
        }}
        onBack={() => setStage('home')}
      />
    );
  }

  if (stage === 'study') {
    return (
      <StudyScreen
        user={user}
        mode={studyMode}
        subject={studySubject}
        circleBatch={studySubject?.circlesBatch || null}
        onBack={() => {
          if (studyMode === 'tajamuat') { setStage('circles'); }
          else if (studyMode === 'تحصيلي') { setStage('subject'); }
          else { setStage('home'); }
        }}
        onDone={() => {
          if (studyMode === 'tajamuat') { setStage('circles'); }
          else { setStage('home'); }
        }}
      />
    );
  }

  if (stage === 'exam') {
    return (
      <ExamScreen
        user={user}
        onBack={() => setStage('home')}
      />
    );
  }

  if (stage === 'wrongAnswers') {
    return (
      <WrongAnswersScreen
        user={user}
        onBack={() => setStage('home')}
      />
    );
  }

  if (stage === 'admin') {
    return (
      <AdminScreen
        user={user}
        theme={theme}
        setTheme={setTheme}
        restrictToQuestions={adminRestricted}
        onHome={() => setStage('home')}
        onLogout={async () => {
          if (heartbeatRef.current) clearInterval(heartbeatRef.current);
          await supabase.auth.signOut();
          setUser(null); setPlan(null); setStage('auth');
        }}
      />
    );
  }

  // home
  return (
    <HomeScreen
      user={user}
      plan={plan}
      theme={theme}
      setTheme={setTheme}
      onAdmin={canAdmin ? () => setStage('admin') : undefined}
      onStartStudy={async (mode) => {
        setStudyMode(mode);
        if (mode === 'tajamuat') {
          setStudySubject(null);
          setStage('tajamuat');
          return;
        }
        // تحقق حي من الاشتراك لكل الأوضاع عدا تجمعات
        if (user?.isGuest) { setStage('plans'); return; }
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (!authUser) { setStage('plans'); return; }
        const { data: sub } = await supabase.from('subscriptions').select('id').eq('user_id', authUser.id).eq('status', 'active').gt('expires_at', new Date().toISOString()).limit(1).maybeSingle();
        if (!sub) { setStage('plans'); return; }
        if (mode === 'تحصيلي') {
          setStudySubject(null);
          setStage('subject');
        } else {
          setStudySubject(null);
          setStage('study');
        }
      }}
      onStartTrial={() => setStage('quiz')}
      onStartExam={async () => {
        if (user?.isGuest) { setStage('plans'); return; }
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (!authUser) { setStage('plans'); return; }
        // المحاكي للباقة الشاملة فقط
        const { data: sub } = await supabase.from('subscriptions').select('id, plans(name)').eq('user_id', authUser.id).eq('status', 'active').gt('expires_at', new Date().toISOString()).limit(1).maybeSingle();
        if (!sub) { setStage('plans'); return; }
        const planName = sub.plans?.name || '';
        if (!['elite','شامل'].includes(planName)) { setStage('plans'); return; }
        setStage('exam');
      }}
      onWrongAnswers={async () => {
        if (user?.isGuest) { setStage('plans'); return; }
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (!authUser) { setStage('plans'); return; }
        // أسئلتي الخاطئة للباقة الشاملة فقط
        const { data: sub } = await supabase.from('subscriptions').select('id, plans(name)').eq('user_id', authUser.id).eq('status', 'active').gt('expires_at', new Date().toISOString()).limit(1).maybeSingle();
        if (!sub) { setStage('plans'); return; }
        const planName = sub.plans?.name || '';
        if (!['elite','شامل'].includes(planName)) { setStage('plans'); return; }
        setStage('wrongAnswers');
      }}
      onPlans={() => setStage('plans')}
      onLogout={async () => {
        if (user?.isGuest) {
          setUser(null); setPlan(null); setStage('auth'); return;
        }
        if (heartbeatRef.current) clearInterval(heartbeatRef.current);
        await supabase.auth.signOut();
        setUser(null); setPlan(null); setQuizScore(null); setStage('auth');
      }}
    />
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <div style={{ position: 'relative' }}>
        <div style={{
          position: 'fixed', bottom: 24, left: 16, zIndex: 99999,
        }}>
          <LangToggle />
        </div>
        <AppInner />
      </div>
    </ErrorBoundary>
  );
}
